// ============================================================
// SecaoTreinosExternos.js — treinos fora dos locais oficiais
//
// Quando o treino de uma modalidade acontece fora do núcleo (outra pista,
// um clube, uma competição), o técnico cadastra aqui o local e o período.
// Os registros de presença daqueles dias deixam de gerar aviso de
// "fora do local".
//
// Duas leituras da mesma tela:
//   - TÉCNICO: cadastra e cancela os treinos da própria modalidade.
//   - COORDENAÇÃO: vê os treinos de todas as modalidades do núcleo.
// Quem pode o quê é decidido no servidor; aqui a interface só esconde o
// formulário de quem não tem modalidade definida.
// ============================================================

import { useState, useEffect, useCallback } from "react";
import { carregarTreinosExternos, salvarTreinoExterno, excluirTreinoExterno, apagarTreinoExterno } from "../firebase";
import { normalizarModalidade } from "../calculo";
import { Card, Btn, Input, StatusBox, SeletorLocal } from "../componentes";
import { AZUL, TEXTO, CINZA_ESC, PERIGO, VERDE_LOGO } from "../tema";

// Fixos, definidos no servidor (functions/index.js). Aqui só para exibir
// e para o texto de ajuda bater com o que a function aceita.
const RAIO_FIXO = 250;
const MAX_DIAS = 31;
const DIAS = [
  { n: 1, rot: "Seg" },
  { n: 2, rot: "Ter" },
  { n: 3, rot: "Qua" },
  { n: 4, rot: "Qui" },
  { n: 5, rot: "Sex" },
];

function hojeISO() {
  const d = new Date();
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().split("T")[0];
}

function fmtData(iso) {
  if (!iso) return "—";
  const p = String(iso).split("-");
  return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : iso;
}

// Um treino já passou quando a data final ficou para trás. Serve só para
// separar visualmente — o histórico continua à vista, porque é ele que
// explica por que certos registros antigos não geraram aviso.
function jaPassou(treino) {
  return (treino.dataFim || "") < hojeISO();
}

export default function SecaoTreinosExternos({ userNucleo, modalidade, ehCoordenacao }) {
  const [treinos, setTreinos] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [mensagem, setMensagem] = useState(null);
  const [salvando, setSalvando] = useState(false);
  const [cancelando, setCancelando] = useState(null);
  const [apagando, setApagando] = useState(null);

  const [nome, setNome] = useState("");
  const [endereco, setEndereco] = useState("");
  const [ponto, setPonto] = useState(null); // { lat, lng } vindo do mapa
  const [dataInicio, setDataInicio] = useState(hojeISO());
  const [dataFim, setDataFim] = useState(hojeISO());
  const [dias, setDias] = useState([]);
  // O técnico não escolhe modalidade: é sempre a dele. Deixar editável
  // permitiria cadastrar treino externo para outra modalidade — o servidor
  // recusaria, mas só depois de o técnico preencher tudo à toa.
  // A coordenação, que atende todas, digita qual é.
  const [modalidadeForm, setModalidadeForm] = useState(modalidade || "");
  const modalidadeTravada = !ehCoordenacao && Boolean(modalidade);

  const recarregar = useCallback(async () => {
    if (!userNucleo) return;
    setCarregando(true);
    try {
      setTreinos(await carregarTreinosExternos(userNucleo));
    } catch (e) {
      setMensagem({ tipo: "erro", msg: "Erro ao carregar treinos externos: " + (e.message || "") });
    } finally {
      setCarregando(false);
    }
  }, [userNucleo]);

  useEffect(() => { recarregar(); }, [recarregar]);
  useEffect(() => { setModalidadeForm(modalidade || ""); }, [modalidade]);

  function alternarDia(n) {
    setDias((atual) => (atual.includes(n) ? atual.filter((d) => d !== n) : [...atual, n].sort()));
  }

  function limparFormulario() {
    setNome(""); setEndereco(""); setPonto(null);
    setDataInicio(hojeISO()); setDataFim(hojeISO());
    setDias([]);
  }

  async function salvar() {
    if (!nome.trim()) {
      setMensagem({ tipo: "erro", msg: "Informe o nome do local do treino." });
      return;
    }
    if (!modalidadeForm.trim()) {
      setMensagem({ tipo: "erro", msg: "Informe a modalidade." });
      return;
    }
    if (!ponto) {
      setMensagem({ tipo: "erro", msg: "Marque o local do treino no mapa." });
      return;
    }
    if (dataFim < dataInicio) {
      setMensagem({ tipo: "erro", msg: "A data de término não pode ser anterior à de início." });
      return;
    }
    const duracaoDias = Math.round(
      (new Date(`${dataFim}T12:00:00`) - new Date(`${dataInicio}T12:00:00`)) / 86400000
    );
    if (duracaoDias > MAX_DIAS) {
      setMensagem({
        tipo: "erro",
        msg: `O período não pode passar de ${MAX_DIAS} dias. Para treino que se repete o ano todo, peça à coordenação para cadastrar o lugar como local oficial do núcleo.`,
      });
      return;
    }

    setSalvando(true);
    setMensagem(null);

    // O servidor recusa local muito longe do núcleo e diz a distância. É
    // a defesa contra o mapa devolver um lugar de mesmo nome em outra
    // cidade, que já deixou uma turma inteira sem registrar presença.
    // Quem cadastra confirma vendo o número, e aí o treino é salvo.
    async function enviar(confirmarDistante) {
      return salvarTreinoExterno({
        nucleo: userNucleo,
        modalidade: modalidadeForm.trim(),
        nome: nome.trim(),
        endereco: endereco.trim(),
        lat: ponto.lat,
        lng: ponto.lng,
        dataInicio,
        dataFim,
        diasSemana: dias,
        confirmarDistante,
      });
    }

    try {
      try {
        await enviar(false);
      } catch (e) {
        const pedeConfirmacao = String(e.message || "").includes("km do núcleo");
        if (!pedeConfirmacao) throw e;
        if (!window.confirm(`${e.message}\n\nConfirmar e cadastrar assim mesmo?`)) {
          setSalvando(false);
          setMensagem({ tipo: "info", msg: "Cadastro cancelado. Marque o local certo no mapa e tente de novo." });
          return;
        }
        await enviar(true);
      }
      setMensagem({ tipo: "ok", msg: "Treino externo cadastrado. Os registros deste período não gerarão aviso de local." });
      limparFormulario();
      await recarregar();
    } catch (e) {
      setMensagem({ tipo: "erro", msg: e.message || "Erro ao cadastrar treino externo." });
    } finally {
      setSalvando(false);
    }
  }

  async function cancelar(treino) {
    setCancelando(treino.id);
    setMensagem(null);
    try {
      await excluirTreinoExterno(treino.id);
      setMensagem({ tipo: "ok", msg: "Treino externo cancelado." });
      await recarregar();
    } catch (e) {
      setMensagem({ tipo: "erro", msg: e.message || "Erro ao cancelar." });
    } finally {
      setCancelando(null);
    }
  }

  async function apagar(treino) {
    setApagando(treino.id);
    setMensagem(null);
    try {
      await apagarTreinoExterno(treino.id);
      setMensagem({ tipo: "ok", msg: "Treino externo excluído definitivamente." });
      await recarregar();
    } catch (e) {
      setMensagem({ tipo: "erro", msg: e.message || "Erro ao excluir." });
    } finally {
      setApagando(null);
    }
  }

  // O técnico vê só os treinos da modalidade dele. A consulta traz os do
  // núcleo inteiro, e sem este filtro o técnico de skate enxergava (e podia
  // cancelar) o treino externo do tênis de mesa.
  //
  // A comparação normaliza os dois lados porque o cadastro tem "BMX" e
  // "Bmx" convivendo, e "Tênis de Mesa" ao lado de "Tenis de Mesa": por
  // igualdade exata, parte dos treinos sumiria do técnico dono deles.
  const daMinhaModalidade = (t) => {
    if (ehCoordenacao || !modalidade) return true;
    if (!t.modalidade) return true; // treino sem modalidade vale para o núcleo
    return normalizarModalidade(t.modalidade) === normalizarModalidade(modalidade);
  };
  const meus = treinos.filter(daMinhaModalidade);

  const ativos = meus.filter((t) => t.ativo !== false && !jaPassou(t));
  const encerrados = meus.filter((t) => t.ativo === false || jaPassou(t));

  function Linha({ t, permiteCancelar, permiteApagar = false }) {
    const inativo = t.ativo === false;
    return (
      <div style={{ border: "1px solid #E5E7EB", borderRadius: 10, padding: 14, background: inativo ? "#F8FAFC" : "#fff" }}>
        <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap", marginBottom: 6 }}>
          <span style={{ fontWeight: 800, fontSize: 14, color: TEXTO }}>{t.nome}</span>
          <span style={{ fontSize: 12, color: CINZA_ESC }}>{t.modalidade}</span>
          {inativo && (
            <span style={{ fontSize: 10.5, fontWeight: 700, padding: "2px 8px", borderRadius: 20, background: "#FEE2E2", color: PERIGO }}>
              CANCELADO
            </span>
          )}
          <span style={{ fontSize: 12, fontWeight: 700, color: AZUL, marginLeft: "auto" }}>
            {fmtData(t.dataInicio)} a {fmtData(t.dataFim)}
          </span>
        </div>

        <div style={{ fontSize: 13, color: CINZA_ESC, lineHeight: 1.6 }}>
          {t.endereco && <div>{t.endereco}</div>}
          <div>
            Raio de {t.raioMetros || RAIO_FIXO} m ·{" "}
            {Array.isArray(t.diasSemana) && t.diasSemana.length > 0
              ? DIAS.filter((d) => t.diasSemana.includes(d.n)).map((d) => d.rot).join(", ")
              : "todos os dias úteis do período"}
          </div>
          {t.criadoPorNome && <div style={{ fontSize: 12 }}>Cadastrado por {t.criadoPorNome}</div>}
          {Number.isFinite(t.lat) && Number.isFinite(t.lng) && (
            <a
              href={`https://www.google.com/maps?q=${t.lat},${t.lng}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{ fontSize: 13, fontWeight: 700, color: "#0891B2", textDecoration: "none" }}
            >
              📍 Ver no mapa
            </a>
          )}
        </div>

        {permiteApagar && (
          <button
            onClick={() => apagar(t)}
            disabled={apagando === t.id}
            style={{ marginTop: 10, background: "#fff", color: PERIGO, border: `1.5px solid ${PERIGO}`, borderRadius: 9, padding: "7px 14px", fontSize: 13, fontWeight: 700, cursor: "pointer" }}
          >
            {apagando === t.id ? "…" : "Excluir definitivamente"}
          </button>
        )}

        {permiteCancelar && !inativo && (
          <button
            onClick={() => cancelar(t)}
            disabled={cancelando === t.id}
            style={{ marginTop: 10, background: "#fff", color: PERIGO, border: `1.5px solid ${PERIGO}`, borderRadius: 9, padding: "7px 14px", fontSize: 13, fontWeight: 700, cursor: "pointer" }}
          >
            {cancelando === t.id ? "…" : "Cancelar treino"}
          </button>
        )}
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <Card>
        <div style={{ fontSize: 16, fontWeight: 800, color: TEXTO, marginBottom: 4 }}>
          Cadastrar treino externo
        </div>
        <div style={{ fontSize: 13, color: CINZA_ESC, marginBottom: 16, lineHeight: 1.5 }}>
          Use quando o treino acontecer fora dos locais oficiais do núcleo. Os registros
          de presença do período deixam de gerar aviso de “fora do local”.
          <b> Cadastre antes do treino</b>: o sistema confere a posição no momento em que a
          presença chega, e um cadastro posterior não desfaz um aviso já aberto.
          O raio é de {RAIO_FIXO} m e o período vai até {MAX_DIAS} dias.
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
            <Input
              label="Nome do local"
              value={nome}
              onChange={setNome}
              placeholder="Ex: Pista do Clube Náutico"
              style={{ minWidth: 240, flex: 1 }}
            />
            {modalidadeTravada ? (
              <div style={{ display: "flex", flexDirection: "column", gap: 5, width: 180 }}>
                <label style={{ fontSize: 13, fontWeight: 600, color: CINZA_ESC }}>Modalidade</label>
                <div style={{
                  border: "1.5px solid #E5E7EB", borderRadius: 10, padding: "9px 12px",
                  fontSize: 14, color: TEXTO, background: "#F1F5F9", fontWeight: 600,
                }}>
                  {modalidade}
                </div>
              </div>
            ) : (
              <Input
                label="Modalidade"
                value={modalidadeForm}
                onChange={setModalidadeForm}
                placeholder="Ex: BMX"
                style={{ width: 180 }}
              />
            )}
          </div>

          <Input
            label="Endereço (opcional)"
            value={endereco}
            onChange={setEndereco}
            placeholder="Ex: Av. Beira Mar, 3000, Fortaleza"
          />

          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: CINZA_ESC, marginBottom: 5 }}>
              Onde será o treino
            </div>
            <SeletorLocal valor={ponto} onChange={setPonto} />
          </div>

          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end" }}>
            <Input label="De" type="date" value={dataInicio} onChange={setDataInicio} style={{ width: 170 }} />
            <Input label="Até" type="date" value={dataFim} onChange={setDataFim} style={{ width: 170 }} />
            <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
              <label style={{ fontSize: 13, fontWeight: 600, color: CINZA_ESC }}>
                Dias da semana (vazio = todos)
              </label>
              <div style={{ display: "flex", gap: 6 }}>
                {DIAS.map((d) => {
                  const on = dias.includes(d.n);
                  return (
                    <button
                      key={d.n}
                      onClick={() => alternarDia(d.n)}
                      style={{
                        border: `1.5px solid ${on ? AZUL : "#D1D5DB"}`,
                        background: on ? AZUL : "#fff",
                        color: on ? "#fff" : CINZA_ESC,
                        borderRadius: 10, padding: "8px 12px", fontSize: 13,
                        fontWeight: 700, cursor: "pointer",
                      }}
                    >
                      {d.rot}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          <div>
            <Btn onClick={salvar} disabled={salvando}>
              {salvando ? "Salvando..." : "Cadastrar treino externo"}
            </Btn>
          </div>
        </div>
      </Card>

      {mensagem && <StatusBox tipo={mensagem.tipo} msg={mensagem.msg} />}

      <Card>
        <div style={{ fontSize: 16, fontWeight: 800, color: TEXTO, marginBottom: 4 }}>
          Treinos externos vigentes
          {ehCoordenacao && <span style={{ fontSize: 12, fontWeight: 600, color: CINZA_ESC }}> · todas as modalidades</span>}
        </div>
        <div style={{ fontSize: 13, color: CINZA_ESC, marginBottom: 14 }}>
          {ativos.length === 0
            ? "Nenhum treino externo vigente no momento."
            : `${ativos.length} treino${ativos.length !== 1 ? "s" : ""} liberando registro fora dos locais oficiais.`}
        </div>

        {carregando ? (
          <div style={{ fontSize: 13, color: CINZA_ESC }}>Carregando...</div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {ativos.map((t) => (
              <Linha key={t.id} t={t} permiteCancelar />
            ))}
          </div>
        )}

        {ativos.length > 0 && (
          <div style={{ fontSize: 12, color: VERDE_LOGO, fontWeight: 600, marginTop: 12 }}>
            Registros nestes locais e datas são considerados regulares.
          </div>
        )}
      </Card>

      {encerrados.length > 0 && (
        <Card>
          <div style={{ fontSize: 15, fontWeight: 800, color: TEXTO, marginBottom: 4 }}>
            Encerrados e cancelados
          </div>
          <div style={{ fontSize: 12, color: CINZA_ESC, marginBottom: 14, lineHeight: 1.5 }}>
            Mantidos à vista porque explicam por que registros antigos daqueles dias não
            geraram aviso.{ehCoordenacao ? " A coordenação pode excluí-los em definitivo quando não fizerem mais falta." : ""}
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {encerrados.map((t) => (
              <Linha key={t.id} t={t} permiteCancelar={false} permiteApagar={ehCoordenacao} />
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
